import * as Sentry from '@sentry/react';
import posthog from 'posthog-js';

import { IGNORED_ERRORS, buildBeforeSend } from './sentry-config';

export interface SentryInitOptions {
  /**
   * The deploy this bundle is running on: `production`, `uat`, `qa1`,
   * `candidate`, `main` or `preview`.
   *
   * Passed in by the app rather than read here, because this package is consumed
   * as source and bundled by each app's Vite config — so a `process.env` read in
   * this file resolves against the app's `define`/`envPrefix`, not against the
   * deploy. That is exactly how it broke: the value was read from
   * `process.env.ANALYTICS_CAPTURE_ENVIRONMENT`, which no app declared, and every
   * event Sentry received carried `environment: null`.
   */
  environment?: string;
  /** Share of transactions sampled for performance. 0 disables tracing. */
  tracesSampleRate?: number;
  /** Share of errored sessions recorded by Session Replay. */
  replaysOnErrorSampleRate?: number;
}

/**
 * Stands in for a missing environment so events still group by deploy.
 *
 * `Sentry.init({ environment: undefined })` produces events with no environment
 * at all, which is indistinguishable from the bug this replaced — an explicit
 * marker is searchable and shows up as a gap to fix.
 */
export const UNKNOWN_ENVIRONMENT = 'unknown';

/**
 * PostHog "Product Data" dataset the Sentry link writes into.
 *
 * https://pypestream.atlassian.net/wiki/spaces/analytics/pages/3632398424/Analytics+Dogfooding+-+Implementation+Details#Sentry-Integration
 */
const POSTHOG_ORGANIZATION = 'pypestream';
const POSTHOG_DATASET_ID = 21;

export const sentryInit = (dsn: string, options: SentryInitOptions = {}) => {
  try {
    if (!dsn) {
      throw new Error('Sentry DSN is not provided');
    }

    Sentry.init({
      dsn,
      environment: options.environment || UNKNOWN_ENVIRONMENT,
      integrations: [
        posthog.sentryIntegration({
          organization: POSTHOG_ORGANIZATION,
          projectId: POSTHOG_DATASET_ID,
        }),
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          // Both apps render customer bot content — message text, variable
          // values, integration names. A replay is only safe to keep while it
          // carries none of it.
          maskAllText: true,
          maskAllInputs: true,
          blockAllMedia: true,
        }),
      ],
      // Only errored sessions are recorded: a replay of an ordinary session
      // costs quota and answers nothing.
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: options.replaysOnErrorSampleRate ?? 1.0,
      tracesSampleRate: options.tracesSampleRate ?? 0.1,
      ignoreErrors: IGNORED_ERRORS,
      beforeSend: buildBeforeSend(),
    });
    console.log(
      '%cSentry: service initialized successfully',
      'color: green; font-size: larger; font-weight: bold'
    );
  } catch (error) {
    console.error('Sentry initialization failed:', error);
  }
};
